import { defineStore } from "pinia";
import apiClient from "../services/api.js";

function deriveBranchCodeFromName(name) {
  const s = String(name ?? "").trim();
  if (!s) return "";
  // Prefer code at the beginning; fallback to first 3 latin letters found.
  const m = s.match(/^[A-Za-z]{3}/) || s.match(/[A-Za-z]{3}/);
  return m ? String(m[0]).toUpperCase() : "";
}

export const useBranchesStore = defineStore("branches", {
  state: () => ({
    items: [], // { branchCode, name, companyName, tax_id, isActive }
    loading: false,
    error: null,
    loadedAt: 0,
  }),
  getters: {
    options(state) {
      // แสดงชื่อสาขา (รหัส) ให้คนอ่านง่าย ถ้าไม่มีชื่อใช้รหัสอย่างเดียว
      // ใช้ branchCode เป็น value เพื่อให้สอดคล้องกับ authStore.branchCodes และ activeBranchStore.active
      const opts = state.items
        .filter((b) => b && (b.isActive === true || String(b.isActive).toLowerCase() === "true"))
        .map((b) => {
          const name = b.name || b.branchName || b.companyName || "";
          const label = name ? `${name} (${b.branchCode})` : `${b.branchCode}`;
          return { label, value: b.branchCode, raw: b };
        });
      return [{ label: "ทุกสาขา (ALL)", value: "ALL" }, ...opts];
    },
    byCode: (state) => (code) => {
      const q = String(code ?? "")
        .trim()
        .toUpperCase();
      return state.items.find(
        (b) =>
          String(b.branchCode ?? "")
            .trim()
            .toUpperCase() === q,
      );
    },
    byId: (state) => (id) => state.items.find((b) => b.id === id || String(b.id) === String(id)),
    byName: (state) => (name) => {
      const q = String(name ?? "")
        .trim()
        .toUpperCase();
      return state.items.find(
        (b) =>
          String(b.name ?? "")
            .trim()
            .toUpperCase() === q,
      );
    },
  },
  actions: {
    async fetch(force = false) {
      if (this.loading) return; // ป้องกันเรียกซ้อน
      if (!force && this.items.length && Date.now() - this.loadedAt < 60_000) return; // cache 60s
      this.loading = true;
      this.error = null;
      try {
        const res = await apiClient.get("/branches");
        const data = res.data?.data || res.data || [];
        // Normalize (เก็บ id ด้วยเพื่อใช้เป็น unique identifier)
        this.items = (Array.isArray(data) ? data : []).map((b) => ({
          id: b.id || b.ID || "",
          name: b.name || b.branchName || b.companyName || "",
          branchCode:
            b.branchCode || b.code || b.id || deriveBranchCodeFromName(b.name || b.branchName || b.companyName) || "",
          companyName: b.companyName || "",
          tax_id: b.tax_id || b.taxId || "",
          isActive: b.isActive === true || String(b.isActive).toLowerCase() === "true",
          isHeadOffice: b.isHeadOffice === true || String(b.isHeadOffice).toLowerCase() === "true",
        }));
        this.loadedAt = Date.now();
      } catch (err) {
        this.error = err?.error || err?.message || "Failed to load branches";
      } finally {
        this.loading = false;
      }
    },
  },
});
